<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import GroupMembersPicker from 'dashboard/components-next/Contacts/Groups/GroupMembersPicker.vue';
import GroupApiNotice from 'dashboard/components-next/Contacts/Groups/GroupApiNotice.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const MAX_GROUP_INVITEES = 8;

const form = reactive({
  inboxId: '',
  name: '',
  description: '',
  memberContactIds: [],
});

const inboxes = useMapGetter('inboxes/getInboxes');

const groupInboxes = computed(() =>
  inboxes.value
    .filter(
      inbox =>
        inbox.channel_type === INBOX_TYPES.WHATSAPP && inbox.groups_supported
    )
    .map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const isFormInvalid = computed(
  () =>
    !form.inboxId ||
    !form.name.trim() ||
    !groupInboxes.value.length ||
    form.memberContactIds.length > MAX_GROUP_INVITEES
);

const resetForm = () => {
  form.inboxId = '';
  form.name = '';
  form.description = '';
  form.memberContactIds = [];
};

const open = () => {
  resetForm();
  store.dispatch('inboxes/get');
  dialogRef.value?.open();
};

const onSuccess = () => {
  resetForm();
  dialogRef.value?.close();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', {
    inboxId: form.inboxId,
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    participantContactIds: form.memberContactIds,
  });
};

defineExpose({ open, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('GROUPS.CREATE.TITLE')"
    :description="t('GROUPS.CREATE.SUBTITLE')"
    :confirm-button-label="t('GROUPS.CREATE.CONFIRM')"
    :disable-confirm-button="isFormInvalid"
    :is-loading="isLoading"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <GroupApiNotice compact />
      <p v-if="!groupInboxes.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('GROUPS.CREATE.INBOX_EMPTY') }}
      </p>
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('GROUPS.CREATE.INBOX_LABEL') }}
        </span>
        <Select
          v-model="form.inboxId"
          class="!w-full [&>select]:w-full"
          :options="groupInboxes"
          :placeholder="t('GROUPS.CREATE.INBOX_PLACEHOLDER')"
          :aria-label="t('GROUPS.CREATE.INBOX_LABEL')"
          :disabled="isLoading || !groupInboxes.length"
        />
      </div>

      <Input
        v-model="form.name"
        :label="t('GROUPS.CREATE.NAME_LABEL')"
        :placeholder="t('GROUPS.CREATE.NAME_PLACEHOLDER')"
        :disabled="isLoading || !groupInboxes.length"
      />

      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('GROUPS.CREATE.MEMBERS_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ `${form.memberContactIds.length}/${MAX_GROUP_INVITEES}` }}
          </span>
        </div>
        <GroupMembersPicker
          v-model="form.memberContactIds"
          :max="MAX_GROUP_INVITEES"
          :placeholder="t('GROUPS.PICKER.PLACEHOLDER')"
          :disabled="isLoading || !groupInboxes.length"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('GROUPS.CREATE.MEMBERS_HINT') }}
        </span>
      </div>

      <TextArea
        v-model="form.description"
        :label="t('GROUPS.CREATE.DESCRIPTION_LABEL')"
        :placeholder="t('GROUPS.CREATE.DESCRIPTION_PLACEHOLDER')"
        :disabled="isLoading || !groupInboxes.length"
        auto-height
      />
    </div>
  </Dialog>
</template>
